import type { AssetReviewItem } from '@volley/crucible-ipc-types';

/**
 * crucible#3187 — pure, React-free carousel-focus logic for the phone asset-
 * review controller. TV is the display (the asset grid + the accent ring on
 * the focused card); the phone drives "currently focused asset id" via
 * swipe/press navigation. Every mutation (Keep it / regenerate / approve all)
 * names its target explicitly (`focusedAssetId` or "every remaining asset in
 * the stage") — there is no separate "which card is focused" IPC channel;
 * both surfaces derive focus from the SAME renderer-local state + the same
 * `art:snapshot` poll, so there is nothing to keep in sync across a wire.
 *
 * Sibling of `asset-stage-view.ts` — same pattern (pure projections, node-env
 * SSR-tested in `__tests__/carousel-focus.test.ts`, no IPC, no React).
 */

/** One carousel card. Currently just `AssetReviewItem` — kept as its own
 *  alias so the carousel's contract with its data source is a named type,
 *  not a structural coincidence, if the two ever need to diverge. */
export type CarouselCard = AssetReviewItem;

/**
 * The ordered card list for the CURRENT stage, from `currentStageView`'s
 * output. `view.assets` is already stage-scoped, poll-order-stable, and
 * (crucially) includes still-generating members once the solo-gate has
 * released (`currentStageView`'s `oneAtATimeStageAssets` only collapses the
 * list to one card during the stage's very first approval) — the carousel
 * can swipe onto a generating card, it just disables actions on it.
 */
export function carouselCards(view: { assets: AssetReviewItem[] } | null): CarouselCard[] {
  return view?.assets ?? [];
}

/**
 * Clamp the focused-asset id across a snapshot poll (#3187): the engine can
 * advance the stage or replace an asset (regen, approval) between two
 * `art:snapshot` ticks, so the id the phone was last focused on may no
 * longer be in `cards`. Resolution, in order:
 *   1. keep the same id if it's still present (the common case — most polls
 *      change asset STATUS, not the stage's membership),
 *   2. else the first non-approved reviewable card (there's still work to do
 *      here — put the focus where a verdict is actually needed),
 *   3. else the first card at all (everything's approved or generating —
 *      land somewhere stable rather than blanking),
 *   4. else `null` (no cards at all — nothing to focus).
 */
export function reconcileFocus(
  cards: readonly CarouselCard[],
  focusedAssetId: string | null,
): string | null {
  if (focusedAssetId !== null && cards.some((card) => card.id === focusedAssetId)) {
    return focusedAssetId;
  }
  const firstActionable = cards.find((card) => card.reviewable && card.status !== 'approved');
  if (firstActionable) return firstActionable.id;
  return cards[0]?.id ?? null;
}

/** Navigation direction: `1` = next card, `-1` = previous card. */
export type StepDirection = 1 | -1;

/**
 * Move focus one step in `cards` — CLAMPED, never wraps (per the design:
 * there is no "past the last card" affordance). An id not currently in
 * `cards` (a stale focus reconciliation should have already fixed this, but
 * defends anyway) resolves to the first card, same as `reconcileFocus`'s
 * final fallback.
 */
export function stepFocus(
  cards: readonly CarouselCard[],
  focusedAssetId: string | null,
  direction: StepDirection,
): string | null {
  if (cards.length === 0) return null;
  const currentIndex = cards.findIndex((card) => card.id === focusedAssetId);
  if (currentIndex === -1) return cards[0]!.id;
  const nextIndex = currentIndex + direction;
  if (nextIndex < 0 || nextIndex >= cards.length) return cards[currentIndex]!.id;
  return cards[nextIndex]!.id;
}

/**
 * crucible#3361 — where focus should land right after Keep-it approves
 * `approvedId`, so repeated "tap Keep it" walks the whole stage without any
 * manual nav. Distinct from `reconcileFocus` (the poll-time CLAMP, which just
 * holds onto `approvedId` because it's still present in `cards` — approving a
 * card flips its `status`, it doesn't remove it from the stage): this is an
 * explicit, deliberate advance the approval path opts into.
 *
 * Resolution, searching forward from `approvedId`'s index and wrapping back
 * to the start (so every open card is eventually visited, never re-checking
 * the same slot twice):
 *   1. the first REVIEWABLE, non-approved card — there's a verdict to give
 *      right now,
 *   2. else the first still-GENERATING, non-approved card — nothing to act on
 *      yet, but Keep it can show "Still creating" instead of stranding focus
 *      on the just-approved card,
 *   3. else `null` — every card is approved; the stage is about to advance
 *      and `reconcileFocus` takes over on the next render.
 */
export function advanceFocusAfterApproval(
  cards: readonly CarouselCard[],
  approvedId: string,
): string | null {
  const approvedIndex = cards.findIndex((card) => card.id === approvedId);
  const startIndex = approvedIndex === -1 ? 0 : approvedIndex + 1;
  const findFrom = (predicate: (card: CarouselCard) => boolean): CarouselCard | undefined => {
    for (let i = 0; i < cards.length; i++) {
      const card = cards[(startIndex + i) % cards.length]!;
      if (predicate(card)) return card;
    }
    return undefined;
  };
  const reviewableOpen = findFrom((card) => card.status !== 'approved' && card.reviewable);
  if (reviewableOpen) return reviewableOpen.id;
  const generatingOpen = findFrom((card) => card.status !== 'approved' && !card.reviewable);
  if (generatingOpen) return generatingOpen.id;
  return null;
}

/** Horizontal drag distance (px) that counts as a deliberate swipe, not an
 *  incidental touch wobble. Matches the "~40px threshold" the design calls
 *  for; the gesture pad's press `<`/`>` targets bypass this entirely (they
 *  call `stepFocus` directly on click, no drag math involved). */
export const SWIPE_THRESHOLD_PX = 40;

/**
 * Resolve a completed horizontal drag/swipe gesture into a step decision.
 * Pure so the drag math is unit-testable without simulating pointer events:
 * the component's pointerup handler just calls this with the accumulated
 * delta and forwards a non-zero result into `stepFocus`.
 *
 * Sign convention: dragging LEFT (content moves left, revealing the next
 * card — the natural "swipe to advance" gesture) is a NEGATIVE deltaX and
 * resolves to `1` (next). Dragging RIGHT resolves to `-1` (previous).
 */
export function resolveSwipeStep(
  deltaX: number,
  thresholdPx: number = SWIPE_THRESHOLD_PX,
): StepDirection | 0 {
  if (deltaX <= -thresholdPx) return 1;
  if (deltaX >= thresholdPx) return -1;
  return 0;
}
